import { CommunicationChannels, FeatureName } from '@bybeam/platform-types';
import FieldDetail from '@platform-ui-common/components/FieldDetail/index.js';
import { IconType } from '@platform-ui-common/components/Icon/Icon.types.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import Loading from '@platform-ui-common/components/Loading/index.js';
import Tooltip from '@platform-ui-common/components/Tooltip/index.js';
import usePartnerPrograms from '@platform-ui-common/hooks/contexts/PartnerPrograms/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { formatAddress, formatIfPhone, mask } from '@platform-ui-common/utilities/format.js';
import { AccountTypesDisplay } from '@platform-ui-common/utilities/payment.js';
import { FullWidthPanel } from '@platform-ui/application/components/index.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import DisplayEmail from '@platform-ui/common/components/DisplayEmail/index.js';
import { POSTHOG_FEATURE_FLAGS } from '@platform-ui/providers/posthog.js';
import { checkFeaturesAnyProgram } from '@platform-ui/utilities/checkFeature.js';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { Callout, Flex, Heading } from '@radix-ui/themes';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import LinkAnonymousAccountModal from './components/LinkAnonymousAccount/LinkAnonymousAccountModal.js';
import { ActionProps, FieldList, Section } from './components/Section.js';
import { UpdatePasswordModal } from './components/UpdatePasswordModal.js';
import { UpdateUserModal } from './components/UpdateUserModal.js';
import UserCommunicationsPreferencesModal from './components/UserCommunicationPreferencesModal.js';
import useProfile from './hooks/useProfile.js';

const CommunicationChannelToLabel: Record<CommunicationChannels, string> = {
  [CommunicationChannels.EMAIL]: 'Emails',
  [CommunicationChannels.SMS]: 'Text Messages',
};

/**
 *
 * @returns Authenticated profile component
 */
function AuthenticatedProfile() {
  const { user, partner, payee, renderApplicantProfileFields, verificationEmail } = useProfile();
  const { programs } = usePartnerPrograms();
  const gcip = user?.gcip;
  const core = user?.core;

  if (!gcip || !core) return null;

  const posthog = usePostHog();
  const hidePasswordSignin = posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.hidePasswordSignin);

  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<
    'updateUser' | 'updatePassword' | 'updateNotifications' | null
  >(null);

  const emailVerified = gcip?.emailVerified;
  const partnerHasMoreThanOneCommChannel =
    Object.values(partner.communicationChannels?.channels || {}).filter((channel) => channel)
      .length > 1;

  const { knownFields, additionalFields } = renderApplicantProfileFields();
  const personalInformationActions: ActionProps[] = [
    ...(hidePasswordSignin
      ? []
      : [
          {
            label: 'Update Password',
            icon: 'lockOpen' as IconType,
            onClick: () => setShowModal('updatePassword'),
          },
        ]),
    { onClick: () => setShowModal('updateUser') },
  ].filter(Boolean);

  if (!emailVerified)
    personalInformationActions.unshift({
      label: 'Verify Email',
      icon: 'markEmailRead',
      onClick: verificationEmail.send,
    });

  return (
    <>
      {showModal === 'updateUser' ? <UpdateUserModal onClose={() => setShowModal(null)} /> : null}
      {showModal === 'updateNotifications' ? (
        <UserCommunicationsPreferencesModal onClose={() => setShowModal(null)} />
      ) : null}
      {showModal === 'updatePassword' ? (
        <UpdatePasswordModal onClose={() => setShowModal(null)} />
      ) : null}
      <div className="p-4 flex flex-col gap-4">
        <Section title="Personal Information" actions={personalInformationActions}>
          <FieldDetail label="name">{core.name ?? gcip.displayName}</FieldDetail>
          <FieldDetail label="email">
            <div className="flex gap-2">
              <DisplayEmail email={gcip.email ?? ''} mailTo={false} />
              {gcip?.emailVerified ? (
                <Tooltip title="You have verified your email">
                  <Icon size="MD" color={Color.Success} type="checkCircle" />
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Your email has not been verified. Click the button above to send a verification email.">
                    <Icon size="MD" color={Color.Warning} type="warning" />
                  </Tooltip>
                </>
              )}
            </div>
          </FieldDetail>
          <FieldDetail label="phone">{core?.phone ? formatIfPhone(core.phone) : '-'}</FieldDetail>
          <FieldDetail label="Mailing Address">
            {core.applicantProfile?.mailingAddress
              ? formatAddress(core.applicantProfile?.mailingAddress)
              : '-'}
          </FieldDetail>
          <FieldDetail label="Tax ID/SSN">{core.taxId ? mask(core.taxId) : '-'}</FieldDetail>
        </Section>
        {partnerHasMoreThanOneCommChannel ? (
          <Section
            title="Communication Preferences"
            actions={[{ onClick: () => setShowModal('updateNotifications') }]}
          >
            {Object.values(CommunicationChannels).map((channel) => (
              <FieldDetail key={channel} label={CommunicationChannelToLabel[channel]}>
                {core?.communicationPreferences?.[channel] === false ? 'disabled' : 'enabled'}
              </FieldDetail>
            ))}
          </Section>
        ) : null}

        {checkFeaturesAnyProgram(programs, [
          FeatureName.PaymentsPartnerIssued,
          FeatureName.PaymentsApplicants,
          FeatureName.PaymentsDirectDeposit,
        ]) && (
          <Section
            title="Financial Information"
            actions={[{ onClick: () => navigate(ClaimRoutes.BANK_ACCOUNT) }]}
          >
            {payee?.loading ? (
              <Loading size="LG" />
            ) : (
              <>
                <FieldDetail label="Routing Number">
                  {payee?.bankAccount?.routingNumber ?? '-'}
                </FieldDetail>
                <FieldDetail label="Account Number">
                  {payee?.bankAccount?.accountNumber ?? '-'}
                </FieldDetail>
                <FieldDetail label="Account Type">
                  {payee?.bankAccount?.accountType
                    ? (AccountTypesDisplay[payee.bankAccount.accountType] ?? '-')
                    : '-'}
                </FieldDetail>
              </>
            )}
          </Section>
        )}
        <Section title="Applicant Information">
          <FieldList>{knownFields}</FieldList>
          <dl className="flex flex-wrap gap-4 -mx-4">{additionalFields}</dl>
        </Section>
      </div>
    </>
  );
}

/**
 * @returns Anonymous profile component
 */
function AnonymousProfile() {
  return (
    <Flex direction="column" gap="2">
      <Callout.Root highContrast>
        <Callout.Icon>
          <InfoCircledIcon />
        </Callout.Icon>
        <Callout.Text>
          You are using a guest account. Link your account to an email address to secure your data
          and access it from any device.
        </Callout.Text>
      </Callout.Root>
      <Flex justify="end">
        <LinkAnonymousAccountModal />
      </Flex>
    </Flex>
  );
}

function Profile() {
  const { user } = useAuth();

  const gcip = user?.gcip;
  const core = user?.core;

  if (!gcip || !core) return null;

  return (
    <FullWidthPanel>
      <Flex direction="column" p="4" gap="4">
        <Heading as="h1">Manage your account</Heading>
        {core?.isAnonymous ? <AnonymousProfile /> : <AuthenticatedProfile />}
      </Flex>
    </FullWidthPanel>
  );
}

export default Profile;
